import { Button } from "@heroui/react";
import { type AdminGoldenAsset, GOLDEN_ASSET_STATUS_LABELS, GoldenAssetStatus } from "@storyline/shared";
import { useState } from "react";
import AssetThumbnail from "@/src/common/components/asset-thumbnail";
import Pill, { type PillTone } from "@/src/common/components/pill";
import type { AssetSlot as AssetSlotModel } from "@/src/series/utils/asset-slots";

type AssetSlotProps = {
	label: string;
	slot: AssetSlotModel;
	isGenerating: boolean;
	onGenerate: () => Promise<void>;
	onApprove: (assetId: string) => Promise<void>;
	onLock: (assetId: string) => Promise<void>;
	onUnlock: (assetId: string) => Promise<void>;
};

const STATUS_TONE: Record<AdminGoldenAsset["status"], PillTone> = {
	[GoldenAssetStatus.CANDIDATE]: "neutral",
	[GoldenAssetStatus.APPROVED]: "success",
	[GoldenAssetStatus.LOCKED]: "accent",
};

const statusBadge = (asset: AdminGoldenAsset) => (
	<Pill tone={STATUS_TONE[asset.status]}>
		v{asset.version} · {GOLDEN_ASSET_STATUS_LABELS[asset.status]}
	</Pill>
);

const AssetSlotView: React.FC<AssetSlotProps> = ({ label, slot, isGenerating, onGenerate, onApprove, onLock, onUnlock }) => {
	const [busy, setBusy] = useState(false);
	const [comparing, setComparing] = useState(false);

	const run = async (action: () => Promise<void>): Promise<void> => {
		setBusy(true);
		try {
			await action();
		} finally {
			setBusy(false);
		}
	};

	const current = slot.current;

	return (
		<div className="flex w-40 shrink-0 flex-col gap-2">
			<AssetThumbnail
				imageUrl={current?.imageUrl ?? null}
				isLoading={isGenerating || busy}
				label={label}
				caption={slot.versions.length > 0 ? `${slot.versions.length} version(s)` : "Aucune version"}
				badge={current ? statusBadge(current) : null}
			/>
			<div className="flex flex-wrap gap-1">
				<Button size="sm" variant="secondary" isDisabled={busy || isGenerating} onPress={() => void run(onGenerate)}>
					{slot.versions.length > 0 ? "Régénérer" : "Générer"}
				</Button>
				{current?.status === GoldenAssetStatus.APPROVED ? (
					<Button size="sm" variant="ghost" isDisabled={busy} onPress={() => void run(() => onLock(current.id))}>
						Verrouiller
					</Button>
				) : null}
				{current?.status === GoldenAssetStatus.LOCKED ? (
					<Button size="sm" variant="ghost" isDisabled={busy} onPress={() => void run(() => onUnlock(current.id))}>
						Déverrouiller
					</Button>
				) : null}
				{slot.candidates.length > 0 ? (
					<Button size="sm" variant="ghost" onPress={() => setComparing((previous) => !previous)}>
						{comparing ? "Fermer" : `Comparer (${slot.candidates.length})`}
					</Button>
				) : null}
			</div>
			{comparing ? (
				<div className="flex flex-wrap gap-2 rounded-xl border border-border-soft bg-glass p-2">
					{slot.candidates.map((candidate) => (
						<div key={candidate.id} className="w-28">
							<AssetThumbnail imageUrl={candidate.imageUrl} badge={statusBadge(candidate)} />
							<Button
								size="sm"
								variant="secondary"
								className="mt-1 w-full"
								isDisabled={busy}
								onPress={() => void run(() => onApprove(candidate.id))}
							>
								Approuver
							</Button>
						</div>
					))}
				</div>
			) : null}
		</div>
	);
};

export default AssetSlotView;
